import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, EffectNotification, ofType, OnRunEffects } from "@ngrx/effects";
import {
    isNavigateToParentCommand,
    isRedoCommand,
    isUndoCommand,
    LessonHubFacade,
} from "@pcp/streaming";
import { EMPTY, Observable } from "rxjs";
import { exhaustMap, filter, map, switchMap } from "rxjs/operators";
import {
    ClassroomConfig,
    ClassroomEffectsHelperService,
    LessonCommandFacade,
    PCP_CLASSROOM_SYNC_CONFIG,
} from "shared/classroom/data-access";
import * as ToolbarActions from "./toolbar.actions";

/**
 * SignalR sync for the toolbar: the beamer relays its toolbar undo/redo/navigate-to-parent
 * clicks to the teacher (emitter side), and the teacher maps the received commands back
 * onto local `ToolbarActions` (consumer side).
 *
 * Toolbar-owned half of the former slide-sync classroom-sync bridge (Plan 06 Part 2);
 * the classroom-owned half lives in `ClassroomSyncConsumerEffects` /
 * `ClassroomSignalrEmitterEffects` (`shared/classroom/data-access`).
 */
@Injectable()
export class ToolbarSignalrSyncEffects implements OnRunEffects {

    private readonly actions$ = inject(Actions);
    private readonly lessonHub = inject(LessonHubFacade);
    private readonly lessonCmd = inject(LessonCommandFacade);
    private readonly classroomHelper = inject(ClassroomEffectsHelperService);
    private readonly config = inject<ClassroomConfig>(PCP_CLASSROOM_SYNC_CONFIG);

    /**
     * Same gate as `ClassroomSignalrEmitterEffects`: emitter role + lesson connected.
     * The consumer-side members below receive hub commands, so they only ever fire
     * while connected anyway; both toolbar-relay roles (teacher receives, beamer
     * sends) run with `isEmitter: true`.
     */
    ngrxOnRunEffects(
        resolvedEffects$: Observable<EffectNotification>
    ): Observable<EffectNotification> {
        if (!this.config.isEmitter) {
            return EMPTY;
        }

        return this.classroomHelper.hasLessonIdAndIsConnected$.pipe(
            switchMap(() => resolvedEffects$)
        );
    }

    readonly onUndoRedoPageCommandReceived$ = createEffect(() =>
        this.lessonHub.Command.pipe(
            filter((cmd) => isUndoCommand(cmd) || isRedoCommand(cmd)),
            map((command) =>
                isUndoCommand(command)
                    ? ToolbarActions.toolbarUndo()
                    : ToolbarActions.toolbarRedo()
            )
        )
    );

    readonly onNavigateToParentCommandReceived$ = createEffect(() =>
        this.lessonHub.Command.pipe(
            filter((cmd) => isNavigateToParentCommand(cmd)),
            map(() => ToolbarActions.toolbarNavigateToParent())
        )
    );

    /**
     * User has clicked on undo/redo in the toolbar.
     * If the classroom is configured to emit toolbar clicks (beamer),
     * send command to the teacher (only) - the teacher will perform undo/redo internally.
     */
    readonly sendToolbarUndoRedo$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    ToolbarActions.toolbarUndo,
                    ToolbarActions.toolbarRedo,
                    ToolbarActions.toolbarNavigateToParent
                ),
                filter((_) => this.config.emitUndoRedo === true),
                exhaustMap((action) => {
                    switch (action.type) {
                        case '[Toolbar] undo button clicked':
                            return this.lessonCmd.sendIntent.undo();
                        case '[Toolbar] redo button clicked':
                            return this.lessonCmd.sendIntent.redo();
                        case '[Toolbar] navigate to parent lection/chapter button clicked':
                            return this.lessonCmd.sendIntent.navigateToParent();
                    }
                })
            ),
        { dispatch: false }
    );
}
